import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { CfFlowResponse } from './api/cloudflareFlowApi';
import { cfEventSubjectOf } from './cfEventProperties';
import { type DomainsProbedThisPage, domainsProbedThisPage } from './cfProbedThisPage';
import { NEVER_LEFT_GAP_MS, cfStepName, isEndedFlow } from './cfStepTrail';
import { useCfFlowEventTracker } from './useCfFlowEventTracker';
import { isReadyEmailType } from './useReadyEmailClicked';

export const FLOW_RESUMED_EVENT = 'Automated CF Integration: Flow Resumed';

/** Marks the last moment this browser had the flow on screen. */
const seenKey = (domain: string) => `cfFlowSeen:${domain}`;
/** Marks the last resume this browser already reported. */
const resumedKey = (domain: string) => `cfFlowResumed:${domain}`;

/**
 * How long one resume stays reported. Two tabs opened together share
 * `localStorage`, so the second finds the first tab's mark and stays quiet.
 */
const RESUME_DEDUP_MS = 60_000;

const read = (key: string): number | null => {
  const raw = window.localStorage.getItem(key);
  const parsed = raw === null ? NaN : Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
};

export type ResumeVisit = {
  lastSeenAt: number | null;
  lastReportedAt: number | null;
  flowCreatedAt: number;
  fromEmail: boolean;
  probedThisPage: boolean;
  flowEnded: boolean;
};

export type ResumeDecision = {
  reports: boolean;
  /** Null when this browser has never seen the flow — a different device. */
  hoursSinceLeft: number | null;
};

/**
 * Whether this mount is a resume, and how long the customer was away.
 *
 * A browser that has never seen an existing flow **is** a resume — that is the
 * journey the flow state exists for, someone opening the propagation email on
 * their phone — but the away time is unknowable there, so it is reported as
 * null rather than as a guess. A flow created within the gap has nothing to
 * resume from unless the email brought the customer here.
 */
export function decideResume(
  { lastSeenAt, lastReportedAt, flowCreatedAt, fromEmail, probedThisPage, flowEnded }: ResumeVisit,
  now: number,
): ResumeDecision {
  if (flowEnded || probedThisPage) return { reports: false, hoursSinceLeft: null };

  if (lastReportedAt !== null && now - lastReportedAt < RESUME_DEDUP_MS) {
    return { reports: false, hoursSinceLeft: null };
  }

  if (lastSeenAt === null) {
    const existedBefore = now - flowCreatedAt >= NEVER_LEFT_GAP_MS;

    return { reports: existedBefore || fromEmail, hoursSinceLeft: null };
  }

  const awayMs = now - lastSeenAt;
  if (awayMs < NEVER_LEFT_GAP_MS) return { reports: false, hoursSinceLeft: null };

  return { reports: true, hoursSinceLeft: Math.round(awayMs / 3_600_000) };
}

export type FlowResumedDependencies = { probedThisPage?: DomainsProbedThisPage };

export const useFlowResumed = (
  flow: CfFlowResponse | undefined,
  { probedThisPage = domainsProbedThisPage }: FlowResumedDependencies = {},
): void => {
  const track = useCfFlowEventTracker(flow && cfEventSubjectOf(flow));
  const [searchParams] = useSearchParams();
  const fromEmail = isReadyEmailType(searchParams.get('email')) || searchParams.get('src') === 'email';

  useEffect(() => {
    if (!flow) return;

    const now = Date.now();
    const resumedStep = cfStepName(flow.screen, flow.status);
    const decision = decideResume(
      {
        lastSeenAt: read(seenKey(flow.domain)),
        lastReportedAt: read(resumedKey(flow.domain)),
        flowCreatedAt: Date.parse(flow.createdAt),
        fromEmail,
        probedThisPage: probedThisPage.includes(flow.domain),
        flowEnded: isEndedFlow(flow.status),
      },
      now,
    );

    if (decision.reports && resumedStep !== null) {
      window.localStorage.setItem(resumedKey(flow.domain), String(now));
      track(FLOW_RESUMED_EVENT, {
        resumed_step: resumedStep,
        // The propagation email carries the marker; anything else is someone
        // returning to the tab or the URL on their own.
        trigger: fromEmail ? 'email' : 'direct',
        hours_since_left: decision.hoursSinceLeft,
      });
    }

    window.localStorage.setItem(seenKey(flow.domain), String(now));
  }, [flow?.domain]);

  // Keep the mark fresh while the screen is open. Without this, a customer who
  // watches the propagation screen for two hours and then reloads it looks like
  // someone who came back after two hours away.
  useEffect(() => {
    if (!flow) return;

    window.localStorage.setItem(seenKey(flow.domain), String(Date.now()));
  }, [flow]);
};
